import { SocialSecuritySortField, PaginatedSocialSecurityQueryVariables } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SortDropdownFilter from "../../Shared/SortDropdownFilter";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: PaginatedSocialSecurityQueryVariables) => void;
}

/**
 * SocialSecuritySortingDropdown component
 * Provides a dropdown menu for sorting social security data by different fields and orders
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting the updated query variables
 * @returns {JSX.Element} Sorting dropdown component
 */
const SocialSecuritySortingDropdown = (props: Props) => {
  const { t } = useTranslation(["translation", "common", "components"]);

  /**
   * Gets user-friendly names for sort fields
   * @param {SocialSecuritySortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: SocialSecuritySortField): string => {
    switch (field) {
    case SocialSecuritySortField.Value:
      return t("components:filters.socialsecurity.sortfields.value");
    case SocialSecuritySortField.ActiveFrom:
      return t("components:filters.socialsecurity.sortfields.activefrom");
    default:
      return field;
    }
  };

  const socialSecuritySortConfig = {
    paginationStateKey: "paginatedSocialSecurity" as keyof PaginationState,
    sortFields: SocialSecuritySortField,
    getSortFieldName,
    defaultSortField: SocialSecuritySortField.ActiveFrom,
    buildQueryVariables: (displayAmount: number, filters: any, sort: { field: SocialSecuritySortField; order: any } | null): PaginatedSocialSecurityQueryVariables => ({
      first: displayAmount,
      filters: {
        activeFrom: filters.activeFrom,
        value: filters.value,
      },
      sort: sort
    }),
    extractFilters: (paginationState: any) => ({
      activeFrom: paginationState.filters?.activeFrom,
      value: paginationState.filters?.value,
    })
  };

  return (
    <SortDropdownFilter
      displayAmount={props.displayAmount}
      handleSubmitClick={props.handleSubmitClick}
      config={socialSecuritySortConfig}
    />
  );
};

export default SocialSecuritySortingDropdown;
